import React, { useCallback, useEffect } from 'react';

import {
  Button,
  Grid,
  Picker,
  IconButton,
  CardHeader,
  CardContent,
  Card,
  HighlightOffIcon,
} from '@gogame/components';
import { FormErrorMessage, withArrayFormField } from '@gogame/form';

import { useExtraContext } from 'scenes/SmartRules/SmartRuleForm/ExtraContext';

import { DynamicCondition } from './DynamicCondition';
import { FormulaCondition } from './FormulaCondition';
import { StaticCondition } from './StaticCondition';
import {
  CONDITIONS,
  STATIC,
  DYNAMIC,
  FORMULA,
  CONDITION_TYPES,
  CONDITION_TYPE,
} from './constants';
import {
  staticConditionValidationSchema,
  dynamicConditionValidationSchema,
  formulaConditionValidationSchema,
} from './schemas';

const schemasByTypes = {
  [STATIC]: staticConditionValidationSchema,
  [DYNAMIC]: dynamicConditionValidationSchema,
  [FORMULA]: formulaConditionValidationSchema,
};

const conditionsByTypes = {
  [STATIC]: StaticCondition,
  [DYNAMIC]: DynamicCondition,
  [FORMULA]: FormulaCondition,
};

export const Conditions = withArrayFormField({
  name: CONDITIONS,
})(({ name, push, remove, form }) => {
  const { isAllowDynamicCondition, hasDynamicConditions } = useExtraContext();
  const handleAddCondition = useCallback(
    ([{ id }]) => {
      push(
        schemasByTypes[id].cast({
          [CONDITION_TYPE]: id,
        })
      );
    },
    [push]
  );

  useEffect(() => {
    if (!isAllowDynamicCondition && hasDynamicConditions) {
      //Clear Dynamic conditions if exist
      form.values?.[CONDITIONS]?.map((condition, index) => ({
        condition,
        index,
      }))
        .filter(({ condition: { [CONDITION_TYPE]: type } }) => type === DYNAMIC)
        .map(({ index }) => index)
        .forEach(remove);
    }
  }, [isAllowDynamicCondition, hasDynamicConditions, remove]);

  const isOptionDisabled = useCallback(
    (option) => {
      return option.id === DYNAMIC && !isAllowDynamicCondition;
    },
    [isAllowDynamicCondition]
  );

  if (!isAllowDynamicCondition && hasDynamicConditions) {
    return null;
  }
  return (
    <React.Fragment>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Picker
            isOptionDisabled={isOptionDisabled}
            button={<Button add>Condition</Button>}
            onChange={handleAddCondition}
            options={CONDITION_TYPES}
          />
        </Grid>
        {form.values?.[CONDITIONS]?.length
          ? form.values?.[CONDITIONS]?.map(
              ({ [CONDITION_TYPE]: conditionType }, i) => (
                <Grid item xs={12} key={`${conditionType}_${i}`}>
                  <Card elevation={6} fullHeight={true}>
                    <CardHeader
                      title={conditionType}
                      action={
                        <IconButton
                          icon={<HighlightOffIcon />}
                          tooltip={'Remove'}
                          onClick={() => remove(i)}
                        />
                      }
                    />

                    <CardContent fullHeight={true} margin={'0 0 16px'}>
                      {React.createElement(conditionsByTypes[conditionType], {
                        name: `${CONDITIONS}.${i}`,
                      })}
                    </CardContent>
                  </Card>
                </Grid>
              )
            )
          : null}
      </Grid>
      <FormErrorMessage name={name} />
    </React.Fragment>
  );
});
